import { FolderPlus, Link2 } from 'lucide-react';
import { useProjects } from '../../hooks/useProjects';
import type { ImportProjectsPreview } from '../../lib/onboardingImportApi';

// The project codes of an onboarding file (04 facturi, 07 reconciliere) that
// are not Firmio's: create the project, or map the code onto an existing one
// (the files are made outside Firmio, so "LGT" may be Firmio's
// "LGT-LET-S-GO-TRAVEL-PLATFORM"). Suggestions come from the server: same
// client, or a name that contains the other.

const NEW = '__new__';

export function ProjectMappingPanel({
  projects,
  projectMap,
  onChange,
  disabled,
}: {
  projects: ImportProjectsPreview;
  projectMap: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  disabled?: boolean;
}) {
  const { data } = useProjects({ sort: 'name', limit: 200, companyId: undefined });
  const all = (data?.projects ?? []) as { id: string; name: string; code?: string | null }[];

  const rows = [
    ...projects.newProjects.map((p) => ({
      code: p.code,
      label: `${p.name}${p.clientName ? ` · ${p.clientName}` : ''}`,
      suggestions: p.suggestions,
      canCreate: true,
    })),
    ...projects.mappedProjects.map((p) => ({
      code: p.code,
      label: p.projectName,
      suggestions: p.suggestions,
      canCreate: false,
    })),
    ...projects.unknownCodes.map((p) => ({
      code: p.code,
      label: 'nu e nici în Firmio, nici în foaia PROIECTE',
      suggestions: p.suggestions,
      canCreate: false,
    })),
  ].sort((a, b) => a.code.localeCompare(b.code));

  if (rows.length === 0 && projects.clientLinks.length === 0) return null;

  function set(code: string, value: string) {
    const next = { ...projectMap };
    if (value === NEW || value === '') delete next[code];
    else next[code] = value;
    onChange(next);
  }

  return (
    <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-3">
      <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
        <FolderPlus className="h-4 w-4 text-brand-600" />
        Proiectele din fișier care nu sunt în Firmio cu același cod
      </p>
      <p className="mt-0.5 text-xs text-gray-600">
        Leagă codul de proiectul existent, dacă e același, ca să nu apară proiecte dublate. Altfel
        se creează proiectul din foaia PROIECTE.
      </p>
      {rows.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {rows.map((r) => {
            const value = projectMap[r.code] ?? (r.canCreate ? NEW : '');
            const suggested = new Set(r.suggestions.map((s) => s.id));
            return (
              <div key={r.code} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="w-28 shrink-0 font-mono text-xs text-gray-700">{r.code}</span>
                <span className="min-w-0 flex-1 truncate text-xs text-gray-500" title={r.label}>
                  {r.label}
                </span>
                <select
                  aria-label={`Proiectul pentru codul ${r.code}`}
                  value={value}
                  disabled={disabled}
                  onChange={(e) => set(r.code, e.target.value)}
                  className="w-64 max-w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs"
                >
                  {r.canCreate ? (
                    <option value={NEW}>Creează proiect nou</option>
                  ) : (
                    <option value="">— alege proiectul —</option>
                  )}
                  {r.suggestions.length > 0 && (
                    <optgroup label="Sugestii">
                      {r.suggestions.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="Toate proiectele">
                    {all
                      .filter((p) => !suggested.has(p.id))
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                  </optgroup>
                </select>
              </div>
            );
          })}
        </div>
      )}
      {projects.clientLinks.length > 0 && (
        <div className="mt-2 text-xs text-gray-600">
          <p className="flex items-center gap-1 font-medium text-gray-700">
            <Link2 className="h-3.5 w-3.5" /> Proiecte fără client care primesc clientul din fișier:
          </p>
          <ul className="ml-5 list-disc">
            {projects.clientLinks.map((l) => (
              <li key={l.projectName}>
                {l.projectName} → {l.clientName}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** The map that takes the server's first suggestion for every code that has one. */
export function suggestedProjectMap(projects: ImportProjectsPreview): Record<string, string> {
  const map: Record<string, string> = {};
  for (const p of projects.newProjects) if (p.suggestions[0]) map[p.code] = p.suggestions[0].id;
  for (const p of projects.unknownCodes) if (p.suggestions[0]) map[p.code] = p.suggestions[0].id;
  return map;
}
